import React, {useEffect, useMemo, useState} from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from 'recharts';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';

import {
  apiGetFundingPerProvincePerCrop,
  IFdFundingPerProvincePerCrop,
} from 'constants/Api';

import './FundingPerProvinceWidget.scss';

/* -------------------------------------------------------------------------- */
const PROVINCE_COLORS = [
  '#267373', // Khula green
  '#f1a24a', // Orange
  '#5b8ed9', // Blue
  '#e57373', // Red
  '#67b67a', // Light green
  '#ab47bc', // Purple
  '#26a69a', // Teal
  '#8d6e63', // Brown
];

/* -------------------------------------------------------------------------- */
const CustomTooltip = ({active, payload}: any) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0];

  return (
    <div className="fpp-tooltip">
      <div className="fpp-tooltip-title">{entry.name}</div>
      <div className="fpp-tooltip-row">
        <span
          className="fpp-dot"
          style={{backgroundColor: entry.payload.fill}}
        />
        <span className="fpp-crop">Funding ask:</span>
        <span className="fpp-val">
          R {Math.round(entry.value).toLocaleString('en-ZA')}
        </span>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
export default function FundingPerProvinceWidget() {
  const [data, setData] = useState<IFdFundingPerProvincePerCrop[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let alive = true;
    apiGetFundingPerProvincePerCrop()
      .then((res) => {
        if (alive) setData(res || []);
      })
      .catch((err) => {
        console.error('Failed to load funding per province', err);
        if (alive) setUnavailable(true);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  // Aggregate total funding per province for pie chart
  const chartData = useMemo(() => {
    const provinceMap: Record<string, number> = {};

    data.forEach((row) => {
      const p = row.province || 'Unknown';
      provinceMap[p] = (provinceMap[p] || 0) + row.totalFunding;
    });

    return Object.keys(provinceMap)
      .map((provinceName, index) => ({
        name: provinceName,
        value: provinceMap[provinceName],
        fill: PROVINCE_COLORS[index % PROVINCE_COLORS.length],
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data]);

  /* ------------------------------------------------------------------------ */
  if (loading) {
    return (
      <Box className="funding-per-province-widget">
        <Typography className="widget-heading">FUNDING BY PROVINCE</Typography>
        <Typography className="widget-sub" variant="caption">
          Total ask broken down by province
        </Typography>
        <Box mt={2}>
          <Skeleton variant="rect" height={220} style={{borderRadius: 8}} />
        </Box>
      </Box>
    );
  }

  return (
    <Box className="funding-per-province-widget">
      <Box className="widget-head">
        <Typography className="widget-heading">FUNDING BY PROVINCE</Typography>
        <Typography className="widget-sub" variant="caption">
          Total ask broken down by province
        </Typography>
      </Box>

      {unavailable ? (
        <Typography variant="body2" color="textSecondary">
          Funding data is not yet available.
        </Typography>
      ) : chartData.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No funding data found
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={75}
              innerRadius={35}
              paddingAngle={2}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{fontSize: 12, paddingTop: 10}}
              iconType="circle"
              iconSize={8}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
    </Box>
  );
}
